import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, CircularProgress, Alert, Chip, Divider,
} from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import { adminService } from '../../services/admin';

/* Read-only list of every domain across a business's web applications.
   Domains are managed on each application's edit page, not from here. */
const BusinessDomainsDialog = ({ business, onClose }) => {
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await adminService.getBusinessDomains(business._id);
      setDomains(Array.isArray(res?.domains) ? res.domains : []);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load domains');
    } finally {
      setLoading(false);
    }
  }, [business._id]);

  useEffect(() => {
    load();
  }, [load]);

  // Group by application, keeping the order the server returned them in.
  const groups = useMemo(() => {
    const map = new Map();
    domains.forEach((d) => {
      if (!map.has(d.appId)) {
        map.set(d.appId, { appId: d.appId, appName: d.appName, appEnabled: d.appEnabled, items: [] });
      }
      map.get(d.appId).items.push(d);
    });
    return [...map.values()];
  }, [domains]);

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Domains — {business.businessName}
        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 400, mt: 0.5 }}>
          All domains configured across this business's web applications
        </Typography>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : error ? (
          <Alert severity="error" action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}>
            {error}
          </Alert>
        ) : groups.length === 0 ? (
          <Alert severity="info">
            No domains yet. Domains are added when creating or editing a web application.
          </Alert>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {groups.map((g) => (
              <Box
                key={g.appId}
                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}
              >
                <Box
                  sx={{
                    px: 2, py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    bgcolor: 'action.hover',
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={700}>{g.appName}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {g.items.length} domain{g.items.length !== 1 ? 's' : ''}
                    {!g.appEnabled ? ' · app disabled' : ''}
                  </Typography>
                </Box>
                {g.items.map((d, i) => (
                  <React.Fragment key={`${g.appId}:${d.domainName}`}>
                    {i > 0 && <Divider />}
                    <Box
                      sx={{
                        px: 2, py: 1.2, display: 'flex', alignItems: 'center', gap: 1.5,
                        opacity: g.appEnabled ? 1 : 0.6,
                      }}
                    >
                      <LanguageIcon sx={{ fontSize: 18, color: '#0369a1' }} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontFamily: 'monospace', fontSize: '0.875rem', fontWeight: 500 }} noWrap>
                          {d.domainName}
                        </Typography>
                        {(d.displayName || d.redirectUrl) && (
                          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                            {[d.displayName, d.redirectUrl].filter(Boolean).join(' · ')}
                          </Typography>
                        )}
                      </Box>
                      <Chip
                        size="small"
                        label={d.enabled ? 'ENABLED' : 'DISABLED'}
                        color={d.enabled ? 'success' : 'default'}
                        variant="outlined"
                        sx={{ fontWeight: 600 }}
                      />
                    </Box>
                  </React.Fragment>
                ))}
              </Box>
            ))}
            <Typography variant="caption" color="text.secondary">
              {domains.length} domain{domains.length !== 1 ? 's' : ''} total
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default BusinessDomainsDialog;
